import { ART_PAL, fade, mix, n, shade, tint, vgrad, type ArtTheme } from '../covers/kit';
import { defineScene, type SceneVariant } from './types';

/**
 * Dragon Gate scene behind the game zone: a sunset gorge — a warm glow low in the sky behind the waterfall board,
 * two cliffs at the sides with a thin waterfall each and a pine on the lip, far peaks in haze, the setting sun beside
 * the right cliff and a few birds; the falls end in mist, and the mist fills the bottom. Nothing starts above the title
 * row or runs under the panels: every motif sits in the free bands beside the board (at 1440 × 900 about x 214…470 and
 * 1130…1386 of the scene). The centre stays calm (only the glow). The phone keeps the sky, the glow, the mist and the
 * cliff edges. No filters, no text. Motifs are marked `data-motif="<x>"` (tests keep them out of the middle 40%).
 */
export function carpScene(theme: ArtTheme, v: SceneVariant): string {
  const P = ART_PAL[theme];
  const p = `sc${theme[0]}${v[0]}`;
  const wide = v === 'wide';
  const W = wide ? 1600 : 400;
  const H = wide ? 900 : 800;
  const o = (dark: number, light: number) => n(P.dark ? dark : light);

  const top = P.dark ? mix(P.bg, P.purple, 0.2) : mix(P.purple, '#FFFFFF', 0.84);
  const mid = P.dark ? mix(P.bg, P.orange, 0.16) : mix(P.orange, '#FFFFFF', 0.8);
  const low = P.dark ? mix(P.bg, P.teal, 0.08) : mix(P.teal, '#FFFFFF', 0.84);
  const sun = P.dark ? mix(P.orange, P.gold, 0.45) : mix(P.orange, P.gold, 0.3);
  const far = P.dark ? mix(P.bg, P.purple, 0.28) : mix(P.purple, '#FFFFFF', 0.7);
  const near = P.dark ? mix(P.bg, P.slate, 0.55) : mix(mix(P.slate, P.purple, 0.35), '#FFFFFF', 0.62);
  const nearHi = P.dark ? mix(near, P.orange, 0.18) : mix(near, P.orange, 0.12);
  const water = P.dark ? mix(P.sky, '#FFFFFF', 0.35) : '#FFFFFF';
  const pine = P.dark ? mix(P.bg, P.green, 0.22) : mix(P.green, P.slate, 0.55);
  const ink = P.dark ? mix(P.bg, P.purple, 0.5) : shade(P.slate, 0.1);

  const defs =
    `<defs>${vgrad(`${p}bg`, top, mid, low)}` +
    `<linearGradient id="${p}ck" x1="0" y1="0" x2="1" y2="1"><stop offset="0" stop-color="${nearHi}"/><stop offset=".6" stop-color="${near}"/></linearGradient>` +
    `<linearGradient id="${p}wf" x1="0" y1="0" x2="0" y2="1"><stop offset="0" stop-color="${water}" stop-opacity="${o(0.55, 0.9)}"/><stop offset="1" stop-color="${water}" stop-opacity="0"/></linearGradient>` +
    `<linearGradient id="${p}ms" x1="0" y1="0" x2="0" y2="1"><stop offset="0" stop-color="${low}" stop-opacity="0"/><stop offset=".55" stop-color="${low}" stop-opacity=".85"/><stop offset="1" stop-color="${low}"/></linearGradient>` +
    fade(`${p}gl`, sun, P.dark ? 0.2 : 0.3) +
    fade(`${p}sp`, water, P.dark ? 0.35 : 0.8) +
    `</defs>`;

  const path = (d: string, fill: string, motif?: number, extra = '') =>
    `<path${motif === undefined ? '' : ` data-motif="${motif}"`} d="${d}" fill="${fill}"${extra}/>`;

  /** a thin waterfall over the lip (x, y) down to `y1`: a sheet that curls over the edge and widens, a few streaks */
  const fall = (x: number, y: number, w: number, y1: number) => {
    const b = w * 0.3;
    const sheet = `M${n(x - 3)} ${y}Q${n(x + w / 2)} ${y - 8} ${n(x + w + 3)} ${y}C${n(x + w + 2)} ${y + 40} ${n(x + w + b)} ${n(y1 - 120)} ${n(x + w + b)} ${y1}H${n(x - b)}C${n(x - b)} ${n(y1 - 120)} ${n(x - 2)} ${y + 40} ${n(x - 3)} ${y}Z`;
    let s = `<g data-motif="${Math.round(x + w / 2)}"><path d="${sheet}" fill="url(#${p}wf)"/><path d="`;
    for (const k of [0.2, 0.5, 0.8])
      s += `M${n(x + w * k)} ${y + 4}Q${n(x + w * k + (k - 0.5) * b)} ${n((y + y1) / 2)} ${n(x + w * k + (k - 0.5) * b * 1.6)} ${n(y1 - (y1 - y) * 0.3 * k)}`;
    s += `" fill="none" stroke="${water}" stroke-opacity="${o(0.35, 0.7)}" stroke-width="1.5"/>`;
    s += `<ellipse cx="${n(x + w / 2)}" cy="${y1}" rx="${n(w * 2.4)}" ry="${n(w * 0.8)}" fill="url(#${p}sp)"/></g>`;
    return s;
  };
  /** a pine on a cliff lip: trunk and three flat tiers */
  const pineAt = (x: number, y: number, s: number, flip: 1 | -1) => {
    const X = (dx: number) => n(x + dx * s * flip);
    const Y = (dy: number) => n(y + dy * s);
    let t = `<path d="M${X(0)} ${Y(0)}C${X(4)} ${Y(-20)} ${X(-6)} ${Y(-40)} ${X(8)} ${Y(-62)}" stroke="${pine}" stroke-width="${n(5 * s)}" fill="none" stroke-linecap="round"/>`;
    for (const [dx, dy, r] of [
      [-4, -30, 26],
      [10, -48, 22],
      [4, -64, 16],
    ] as const)
      t += `<ellipse cx="${X(dx)}" cy="${Y(dy)}" rx="${n(r * s)}" ry="${n(r * 0.32 * s)}" fill="${pine}"/>`;
    return `<g data-motif="${Math.round(x)}" opacity="${o(0.95, 0.85)}">${t}</g>`;
  };
  const bird = (x: number, y: number, s: number) =>
    `M${n(x - 9 * s)} ${n(y - 3 * s)}Q${n(x - 4 * s)} ${n(y - 6 * s)} ${x} ${y}Q${n(x + 4 * s)} ${n(y - 6 * s)} ${n(x + 9 * s)} ${n(y - 3 * s)}`;

  let art =
    `<rect width="${W}" height="${H}" fill="url(#${p}bg)"/>` +
    `<ellipse cx="${W / 2}" cy="${n(H * 0.42)}" rx="${n(W * 0.42)}" ry="${n(H * 0.36)}" fill="url(#${p}gl)"/>`;

  if (wide) {
    art +=
      // the setting sun beside the right cliff, its glow, a far haze of peaks on both sides
      `<g data-motif="1215"><circle cx="1215" cy="232" r="120" fill="url(#${p}gl)"/><circle cx="1215" cy="232" r="34" fill="${sun}" fill-opacity="${o(0.55, 0.6)}"/></g>` +
      `<g fill="${far}" fill-opacity="${o(0.8, 0.7)}">` +
      path('M0 900V150L70 120L140 165L205 130L290 190L360 170L430 260L480 420L500 900Z', far, 250) +
      path('M1600 900V170L1520 135L1450 180L1390 250L1330 238L1250 300L1160 430L1120 900Z', far, 1380) +
      `</g>` +
      // near cliffs with the lips the falls come from
      path(
        'M0 900V210L60 195L120 214L170 200L232 234L300 222L352 236C392 300 420 420 440 540L456 900Z',
        `url(#${p}ck)`,
        200,
      ) +
      path(
        'M1600 900V246L1540 230L1470 256L1400 240L1330 266L1262 262C1215 330 1186 450 1170 570L1150 900Z',
        `url(#${p}ck)`,
        1390,
      ) +
      fall(306, 226, 34, 700) +
      fall(1266, 266, 32, 720) +
      pineAt(248, 232, 1.05, 1) +
      `<g data-motif="1330"><path d="${bird(1300, 140, 1)}${bird(1335, 124, 0.8)}${bird(1356, 150, 0.7)}" fill="none" stroke="${ink}" stroke-opacity="${o(0.7, 0.55)}" stroke-width="2" stroke-linecap="round"/></g>` +
      `<g data-motif="330"><path d="${bird(310, 120, 0.8)}${bird(340, 108, 0.6)}" fill="none" stroke="${ink}" stroke-opacity="${o(0.7, 0.55)}" stroke-width="2" stroke-linecap="round"/></g>`;
  } else {
    // the phone: only the cliff edges beside the board, far peaks and one thin fall at the left edge
    art +=
      path('M0 800V180L22 170L40 230L34 800Z', far, undefined, ` fill-opacity="${o(0.8, 0.7)}"`) +
      path('M400 800V200L378 190L360 250L366 800Z', far, undefined, ` fill-opacity="${o(0.8, 0.7)}"`) +
      path('M0 800V250L14 244L26 300L22 800Z', `url(#${p}ck)`) +
      path('M400 800V270L386 264L374 320L378 800Z', `url(#${p}ck)`) +
      fall(6, 250, 9, 600);
  }

  art += `<rect y="${n(H * 0.7)}" width="${W}" height="${n(H * 0.3)}" fill="url(#${p}ms)"/>`;

  return (
    `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 ${W} ${H}" preserveAspectRatio="xMidYMid slice">` +
    defs +
    art +
    `</svg>`
  );
}

/** Header accent of Dragon Gate: a soft wave (repeat-x tile, 16 × 8) in water and sunset colours. */
export function carpAccent(theme: ArtTheme): string {
  const P = ART_PAL[theme];
  const water = P.dark ? mix(P.sky, P.teal, 0.4) : P.teal;
  const sun = P.dark ? mix(P.orange, P.gold, 0.4) : tint(P.orange, 0.1);
  return (
    `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 16 8" width="16" height="8">` +
    `<path d="M0 7.5H16" stroke="${sun}" stroke-opacity="${P.dark ? 0.6 : 0.75}"/>` +
    `<path d="M0 4.5Q4 1.5 8 4.5T16 4.5" fill="none" stroke="${water}" stroke-opacity="${P.dark ? 0.75 : 0.85}" stroke-width="1.2"/>` +
    `</svg>`
  );
}

export default defineScene({ game: 'carp', scene: carpScene, accent: carpAccent });
